/* eslint-disable max-lines-per-function */
import { injectService } from '@patsnap/tahiti-core/ioc/inject';
import { computed, ref } from 'vue';
import { useDisposes } from '@telecom/shared/composables/use-disposes';
import { createSharedComposable } from '@vueuse/core';
import { SpecificationTaskCreateService } from '@/domains/specification/services/specification-task-create-service';
import type {
    SpecificationJurisdictionOption,
    SpecificationTaskCreatePayload,
    SpecificationTaskCreatePayloadErrors,
    SpecificationTaskCreateState,
    SpecificationTaskCreateSubmitResult,
} from '@/domains/specification/types/specification-task-create';
import { CurrentTaskService } from '@/services/current-task/current-task-service';
import _ from 'lodash';
import { SharePageService } from '@/services/share-page/share-page-service';
import { SpecificationJurisdiction } from '@/domains/specification/enums';

/**
 * A composable that provides state management and actions for creating a specification task.
 * It is a shared composable, meaning that its state is singleton across the application.
 */
export const useSpecificationTaskCreateService = createSharedComposable(() => {
    const service = injectService<SpecificationTaskCreateService>(SpecificationTaskCreateService);
    const currentTaskService = injectService<CurrentTaskService>(CurrentTaskService);
    const sharePageService = injectService<SharePageService>(SharePageService);

    const { disposes } = useDisposes();
    /**
     * Indicates if the service has been initialized and is ready to use.
     */
    const isReady = ref(false);

    /**
     * The reactive state of the specification task creation form.
     */
    const state = ref<SpecificationTaskCreateState>(service.getState());

    disposes.push(
        service.onStateChange(_state => {
            state.value = _state;
        }),
    );

    /**
     * A computed property that determines if the submit button should be disabled.
     * The button is disabled if there are form errors or if a submission is already in progress.
     */
    const submitDisabled = computed(() => {
        return !_.isEmpty(state.value.formErrors) || state.value.loadingSubmit;
    });

    /**
     * Initializes the service. This function should be called before using the composable.
     * It initializes the share page service, sets up the task creation service with the current task ID,
     * and performs an initial validation of the form data.
     */
    async function init() {
        isReady.value = false;
        await sharePageService.initialize();
        await service.init(currentTaskService.taskId);
        // 初始化完成后校验一次表单数据
        await service.validateFormData();
        isReady.value = true;
    }

    /**
     * Sets a partial payload of the form data and triggers validation.
     * @param payload - The partial form data to be updated.
     */
    function setFormData(payload: Partial<SpecificationTaskCreatePayload>) {
        service.setFormData(payload);
        service.validateFormData();
    }

    /**
     * Manually triggers the validation of the entire form data.
     * @returns A promise that resolves with the validation errors.
     */
    async function validateFormData(): Promise<SpecificationTaskCreatePayloadErrors> {
        return service.validateFormData();
    }

    /**
     * Gets the current form validation errors.
     * @returns The current form errors.
     */
    function getFormErrors(): SpecificationTaskCreatePayloadErrors {
        return service.getFormErrors();
    }

    /**
     * Gets the list of available jurisdiction options for the form.
     * @returns An array of jurisdiction options.
     */
    function getJurisdictions(): SpecificationJurisdictionOption[] {
        return service.getJurisdictions();
    }

    /**
     * Submits the form data to create a new specification task.
     * @returns A promise that resolves with the result of the submission.
     */
    async function submit(): Promise<SpecificationTaskCreateSubmitResult> {
        return service.submit();
    }

    /**
     * A computed property that returns the currently selected jurisdiction from the form data.
     */
    const jurisdiction = computed(() => {
        return state.value.formData.jurisdiction;
    });

    const isCNIPA = computed(() => {
        return jurisdiction.value === SpecificationJurisdiction.CN;
    });

    return {
        /**
         * Indicates if the service has been initialized and is ready to use.
         */
        isReady,
        /**
         * The reactive state of the specification task creation form.
         */
        state,
        /**
         * A computed property that determines if the submit button should be disabled.
         */
        submitDisabled,
        /**
         * Initializes the service.
         */
        init,
        /**
         * Sets a partial payload of the form data and triggers validation.
         */
        setFormData,
        /**
         * Manually triggers the validation of the entire form data.
         */
        validateFormData,
        /**
         * Gets the current form validation errors.
         */
        getFormErrors,
        /**
         * Gets the list of available jurisdiction options for the form.
         */
        getJurisdictions,
        /**
         * Submits the form data to create a new specification task.
         */
        submit,
        /**
         * A computed property that returns the currently selected jurisdiction.
         */
        jurisdiction,
        /**
         * A computed property that returns if the currently selected jurisdiction is CNIPA.
         */
        isCNIPA,
    };
});
